import { useState, useRef, useCallback, useEffect } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import api from '@/utils/api';

// Enable Agora SDK logging in development
if (process.env.NODE_ENV === 'development') {
  AgoraRTC.setLogLevel(1);
}

// Module-level map to track active clients per stream (persists across StrictMode remounts)
const activeClients = new Map();
const joinLocks = new Map();

/**
 * Custom hook for Agora video streaming
 * Handles both broadcaster (host) and viewer roles
 */
export const useAgora = (streamId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [connectionState, setConnectionState] = useState('DISCONNECTED');
  
  const clientRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const isInitializedRef = useRef(false);
  const joinedRef = useRef(false);
  const isJoiningRef = useRef(false); // New flag to prevent concurrent join attempts

  // Initialize Agora client
  const initClient = useCallback((role = 'host') => {
    if (clientRef.current) {
      return clientRef.current;
    }

    const client = AgoraRTC.createClient({ 
      codec: 'vp8', 
      mode: 'live'
    });

    // Set up event listeners
    client.on('user-published', async (user, mediaType) => {
      console.log('[Agora] User published:', user.uid, mediaType);
      try {
        await client.subscribe(user, mediaType);
        console.log('[Agora] Subscribed to user:', user.uid, mediaType);
        
        if (mediaType === 'video') {
          setRemoteUsers(prev => {
            const exists = prev.find(u => u.uid === user.uid);
            if (exists) {
              return prev.map(u => u.uid === user.uid ? { ...u, videoTrack: user.videoTrack } : u);
            }
            return [...prev, { uid: user.uid, videoTrack: user.videoTrack, audioTrack: user.audioTrack }];
          });
        }
        
        if (mediaType === 'audio') {
          user.audioTrack?.play();
          setRemoteUsers(prev => 
            prev.map(u => u.uid === user.uid ? { ...u, audioTrack: user.audioTrack } : u)
          );
        }
      } catch (err) {
        console.error('[Agora] Subscribe error:', err);
      }
    });

    client.on('user-unpublished', (user, mediaType) => {
      console.log('[Agora] User unpublished:', user.uid, mediaType);
      if (mediaType === 'video') {
        setRemoteUsers(prev => 
          prev.map(u => u.uid === user.uid ? { ...u, videoTrack: null } : u)
        );
      }
    });

    client.on('user-left', (user) => {
      console.log('[Agora] User left:', user.uid);
      setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
    });

    client.on('connection-state-change', (curState, prevState) => {
      console.log('[Agora] Connection state:', prevState, '->', curState);
      setConnectionState(curState);
      setIsConnected(curState === 'CONNECTED');
    });

    client.on('exception', (event) => {
      console.error('[Agora] Exception:', event);
    });

    clientRef.current = client;
    isInitializedRef.current = true;
    return client;
  }, []);

  // Get Agora token from backend
  const getToken = useCallback(async () => {
    try {
      const response = await api.post(`/live/streams/${streamId}/token`);
      return response.data;
    } catch (err) {
      console.error('[Agora] Token fetch error:', err);
      throw new Error('Failed to get streaming token');
    }
  }, [streamId]);

  // Join channel as host (broadcaster)
  const joinAsHost = useCallback(async (videoContainer) => {
    if (!streamId) {
      setError('No stream ID provided');
      return false;
    }
    
    // Check module-level lock first (persists across StrictMode remounts)
    if (joinLocks.get(streamId)) {
      console.log('[Agora] Join lock active for stream, skipping host join...');
      return true;
    }
    
    // Check if there's already an active client for this stream
    const existingClient = activeClients.get(streamId);
    if (existingClient && (existingClient.connectionState === 'CONNECTING' || existingClient.connectionState === 'CONNECTED')) {
      console.log('[Agora] Using existing client in', existingClient.connectionState, 'state');
      clientRef.current = existingClient;
      return true;
    }
    
    // Prevent double joins using synchronous ref check
    if (isJoiningRef.current || joinedRef.current) {
      console.log('[Agora] Already joining or joined, skipping host join...');
      return true;
    }
    
    // Also check client state directly
    if (clientRef.current) {
      const state = clientRef.current.connectionState;
      if (state === 'CONNECTING' || state === 'CONNECTED') {
        console.log('[Agora] Client already in', state, 'state, skipping...');
        return true;
      }
    }
    
    // Set module-level lock
    joinLocks.set(streamId, true);
    isJoiningRef.current = true;

    try {
      setError(null);
      const client = initClient('host');
      
      // Store client in module-level map
      activeClients.set(streamId, client);
      
      // Double check after getting client
      if (client.connectionState === 'CONNECTING' || client.connectionState === 'CONNECTED') {
        console.log('[Agora] Client state check failed after init:', client.connectionState);
        isJoiningRef.current = false;
        joinLocks.set(streamId, false);
        return true;
      }
      
      // Get token from backend
      const tokenData = await getToken();
      console.log('[Agora] Token received:', { ...tokenData, token: '***' });

      // Set role to host
      await client.setClientRole('host');

      // Join the channel
      await client.join(
        tokenData.app_id,
        tokenData.channel_name,
        tokenData.token,
        tokenData.uid
      );
      joinedRef.current = true;
      console.log('[Agora] Joined channel as host');

      // Create local tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
        {
          encoderConfig: 'music_standard'
        },
        {
          encoderConfig: {
            width: 720,
            height: 1280,
            frameRate: 30,
            bitrateMin: 1000,
            bitrateMax: 3000
          },
          facingMode: 'user'
        }
      );

      localAudioTrackRef.current = audioTrack;
      localVideoTrackRef.current = videoTrack;

      // Play local video
      if (videoContainer) {
        videoTrack.play(videoContainer);
      }

      // Publish tracks
      await client.publish([audioTrack, videoTrack]);
      setIsPublishing(true);
      console.log('[Agora] Published local tracks');

      return true;
    } catch (err) {
      console.error('[Agora] Join as host error:', err);
      setError(err.message || 'Failed to start broadcasting');
      isJoiningRef.current = false;
      joinLocks.set(streamId, false);
      return false;
    }
  }, [streamId, initClient, getToken]);

  // Join channel as viewer (audience)
  const joinAsViewer = useCallback(async () => {
    if (!streamId) {
      setError('No stream ID provided');
      return false;
    }
    
    // Check module-level lock first (persists across StrictMode remounts)
    if (joinLocks.get(streamId)) {
      console.log('[Agora] Join lock active for stream, skipping viewer join...');
      return true;
    }
    
    // Check if there's already an active client for this stream
    const existingClient = activeClients.get(streamId);
    if (existingClient && (existingClient.connectionState === 'CONNECTING' || existingClient.connectionState === 'CONNECTED')) {
      console.log('[Agora] Using existing client in', existingClient.connectionState, 'state');
      clientRef.current = existingClient;
      return true;
    }
    
    // Prevent double joins using synchronous ref check
    if (isJoiningRef.current || joinedRef.current) {
      console.log('[Agora] Already joining or joined, skipping viewer join...');
      return true;
    }
    
    // Also check client state directly
    if (clientRef.current) {
      const state = clientRef.current.connectionState;
      if (state === 'CONNECTING' || state === 'CONNECTED') {
        console.log('[Agora] Client already in', state, 'state, skipping...');
        return true;
      }
    }
    
    // Set module-level lock
    joinLocks.set(streamId, true);
    isJoiningRef.current = true;

    try {
      setError(null);
      const client = initClient('audience');
      
      // Store client in module-level map
      activeClients.set(streamId, client);
      
      // Double check after getting client
      if (client.connectionState === 'CONNECTING' || client.connectionState === 'CONNECTED') {
        console.log('[Agora] Client state check failed after init:', client.connectionState);
        isJoiningRef.current = false;
        joinLocks.set(streamId, false);
        return true;
      }
      
      // Get token from backend
      const tokenData = await getToken();
      console.log('[Agora] Token received for viewer:', { ...tokenData, token: '***' });

      // Set role to audience
      await client.setClientRole('audience');

      // Join the channel
      await client.join(
        tokenData.app_id,
        tokenData.channel_name,
        tokenData.token,
        tokenData.uid
      );
      joinedRef.current = true;
      console.log('[Agora] Joined channel as viewer');

      return true;
    } catch (err) {
      console.error('[Agora] Join as viewer error:', err);
      // Only set error if it's not a duplicate join error
      if (!err.message?.includes('already in connecting')) {
        setError(err.message || 'Failed to join stream');
      }
      isJoiningRef.current = false;
      joinLocks.set(streamId, false);
      return false;
    }
  }, [streamId, initClient, getToken]);

  // Toggle camera
  const toggleCamera = useCallback(async () => {
    if (localVideoTrackRef.current) {
      const enabled = localVideoTrackRef.current.enabled;
      await localVideoTrackRef.current.setEnabled(!enabled);
      return !enabled;
    }
    return false;
  }, []);

  // Toggle microphone
  const toggleMicrophone = useCallback(async () => {
    if (localAudioTrackRef.current) {
      const enabled = localAudioTrackRef.current.enabled;
      await localAudioTrackRef.current.setEnabled(!enabled);
      return !enabled;
    }
    return false;
  }, []);

  // Switch camera (front/back) - for mobile devices
  const switchCamera = useCallback(async () => {
    if (localVideoTrackRef.current) {
      try {
        // Try using the built-in setDevice method with facingMode
        const devices = await AgoraRTC.getCameras();
        
        if (devices.length < 2) {
          console.log('[Agora] Only one camera available');
          return false;
        }
        
        // Get current device ID (try multiple ways)
        let currentDeviceId = null;
        if (localVideoTrackRef.current.getMediaStreamTrack) {
          const track = localVideoTrackRef.current.getMediaStreamTrack();
          const settings = track?.getSettings();
          currentDeviceId = settings?.deviceId;
        }
        
        // If we couldn't get current device, use the internal property as fallback
        if (!currentDeviceId && localVideoTrackRef.current._deviceId) {
          currentDeviceId = localVideoTrackRef.current._deviceId;
        }
        
        // Find current camera index and switch to next
        let nextDeviceId;
        if (currentDeviceId) {
          const currentIndex = devices.findIndex(d => d.deviceId === currentDeviceId);
          const nextIndex = (currentIndex + 1) % devices.length;
          nextDeviceId = devices[nextIndex].deviceId;
        } else {
          // Can't determine current camera, just pick the second one
          nextDeviceId = devices[1]?.deviceId || devices[0]?.deviceId;
        }
        
        if (nextDeviceId) {
          await localVideoTrackRef.current.setDevice(nextDeviceId);
          console.log('[Agora] Camera switched successfully');
          return true;
        }
      } catch (err) {
        console.error('[Agora] Switch camera error:', err);
        // On mobile, try recreating the track with different facingMode
        try {
          if (clientRef.current && localVideoTrackRef.current) {
            // Get current facing mode
            const track = localVideoTrackRef.current.getMediaStreamTrack?.();
            const settings = track?.getSettings?.();
            const currentFacing = settings?.facingMode || 'user';
            const newFacing = currentFacing === 'user' ? 'environment' : 'user';
            
            // Unpublish current track
            await clientRef.current.unpublish(localVideoTrackRef.current);
            localVideoTrackRef.current.stop();
            localVideoTrackRef.current.close();
            
            // Create new track with opposite facing mode
            const newVideoTrack = await AgoraRTC.createCameraVideoTrack({
              encoderConfig: {
                width: 720,
                height: 1280,
                frameRate: 30,
                bitrateMin: 1000,
                bitrateMax: 3000
              },
              facingMode: newFacing
            });
            
            localVideoTrackRef.current = newVideoTrack;
            await clientRef.current.publish(newVideoTrack);
            
            // Replay video in container
            const container = document.getElementById('agora-video-player') || 
                            document.querySelector('[data-testid="mobile-broadcaster"] > div > div');
            if (container) {
              newVideoTrack.play(container);
            }
            
            console.log('[Agora] Camera switched via track recreation');
            return true;
          }
        } catch (recreateErr) {
          console.error('[Agora] Failed to recreate camera track:', recreateErr);
        }
      }
    }
    return false;
  }, []);

  // Leave channel
  const leave = useCallback(async () => {
    try {
      // Stop and close local tracks
      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
        localAudioTrackRef.current = null;
      }
      
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
        localVideoTrackRef.current = null;
      }

      // Leave channel
      if (clientRef.current && joinedRef.current) {
        await clientRef.current.leave();
      }
      
      // Reset refs
      joinedRef.current = false;
      isJoiningRef.current = false;
      
      // Clean up module-level tracking (but keep the lock for a short time to prevent immediate re-join)
      activeClients.delete(streamId);
      // Don't immediately release the lock - let it expire naturally after a delay
      setTimeout(() => {
        joinLocks.delete(streamId);
      }, 1000);

      setIsConnected(false);
      setIsPublishing(false);
      setRemoteUsers([]);
      console.log('[Agora] Left channel');
    } catch (err) {
      console.error('[Agora] Leave error:', err);
    }
  }, [streamId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      leave();
    };
  }, [leave]);

  return {
    // State
    isConnected,
    isPublishing,
    error,
    remoteUsers,
    connectionState,
    
    // Methods
    joinAsHost,
    joinAsViewer,
    toggleCamera,
    toggleMicrophone,
    switchCamera,
    leave,
    
    // Refs for direct access
    localVideoTrack: localVideoTrackRef.current,
    localAudioTrack: localAudioTrackRef.current,
  };
};

export default useAgora;
